import React, { useMemo, useState } from 'react';
import Intl from '@fx-ui/intl';
import { InvitationInfo, PersonaInfo, SuiteId } from '~/interfaces';
import {
    filter,
    getCurrentSuiteId,
    getResolvedSuiteViews,
    getUrlParameter,
    isEmpty,
    isMobileUA,
    isNumber,
    map,
    size
} from '~/utils';
import { URL_PARAMETER } from '~/constants';
import AccountPageLayout from '~envs/mixins/account/containers/account-page-layout';
import RegisterTitle from '~/mixins/account/components/register-title';
import RegisterForm from '~/mixins/account/containers/register-form';
import CorpPersonaCollect from '~/mixins/account/containers/corp-persona-collect';
import UserPersonaCollect from '~/mixins/account/containers/user-persona-collect';
import usePersonaLabel from '~/mixins/account/hooks/usePersonaLabel';
import { isAccountInvitation, isInvitationMode } from '~/mixins/account/utils/invite';
import { handleRedirect } from '~/mixins/account/utils/register';
import MemberInviteAcceptPane from '~/mixins/account/containers/member-invite-accept-pane';
import RegisterStep, { REGISTER_STEP } from './components/register-step';
import { RegisterProps } from '../../../../interfaces';
import { SuiteSelectList } from '~/mixins/account/containers/suite-select-list';
import AccountBackButton from '~/mixins/account/components/account-back-button';
import { redirectToLogin } from '~/mixins/account/utils/login';
import classNames from 'classnames';

const DEFAULT_PERSONA_INFO = {
    corpName: getUrlParameter(URL_PARAMETER.CORP_NAME)
};

const Register: React.FC<RegisterProps> = (props) => {
    const { skipRegister, invitations, onComplete } = props;

    const [personaInfo, setPersonaInfo] = useState<PersonaInfo>(DEFAULT_PERSONA_INFO);
    const [registerStep, setRegisterStep] = useState<REGISTER_STEP>(
        skipRegister ? REGISTER_STEP.CORP_PERSONA : REGISTER_STEP.REGISTER
    );
    const [invitationList, setInvitationList] = useState<InvitationInfo[] | undefined>(invitations);
    const [currentSuiteId, setCurrentSuiteId] = useState<SuiteId>(getCurrentSuiteId);
    const [backSuiteSelectVisible, setBackSuiteSelectVisible] = useState(false);

    const suiteViewList = useMemo(() => {
        const suiteViewMap = getResolvedSuiteViews();
        return filter(
            Object.values(suiteViewMap),
            (suite) =>
                isNumber(suite.suite_id) && suite.suite_id !== SuiteId.PLATFORM && !suite.is_beta
        );
    }, []);

    const showSuiteSelect = currentSuiteId === SuiteId.PLATFORM && size(suiteViewList) > 1;

    const personaLabel = usePersonaLabel({
        enabled: registerStep !== REGISTER_STEP.REGISTER && isEmpty(invitationList),
        suiteId: currentSuiteId
    });

    if (!isEmpty(invitationList)) {
        return (
            <MemberInviteAcceptPane
                invites={invitationList!}
                onCancel={() => {
                    setRegisterStep(REGISTER_STEP.CORP_PERSONA);
                    setInvitationList(undefined);
                }}
            />
        );
    }

    const renderBack2SuiteSelectBtn = () => {
        return (
            <AccountBackButton
                name={Intl.get('fxp.base.workspace.login.prod_selected.ttl')}
                onClick={() => {
                    setCurrentSuiteId(SuiteId.PLATFORM);
                    setBackSuiteSelectVisible(false);
                    setPersonaInfo(DEFAULT_PERSONA_INFO);
                }}
            />
        );
    };

    const renderRegisterStepContent = () => {
        switch (registerStep) {
            case REGISTER_STEP.REGISTER: {
                return (
                    <>
                        {backSuiteSelectVisible && renderBack2SuiteSelectBtn()}
                        <RegisterTitle hasLoginSwitch={!isAccountInvitation} />
                        <RegisterForm
                            suiteId={currentSuiteId}
                            onComplete={({ invitations }) => {
                                if (isEmpty(invitations)) {
                                    setRegisterStep(REGISTER_STEP.CORP_PERSONA);
                                } else {
                                    setInvitationList(invitations);
                                }
                            }}
                        />
                    </>
                );
            }
            case REGISTER_STEP.CORP_PERSONA: {
                return (
                    <>
                        {skipRegister && backSuiteSelectVisible && renderBack2SuiteSelectBtn()}
                        <CorpPersonaCollect
                            personaInfo={personaInfo}
                            personaLabel={personaLabel}
                            setPersonaInfo={setPersonaInfo}
                            suiteId={currentSuiteId}
                            onComplete={() => {
                                setRegisterStep(REGISTER_STEP.USER_PERSONA);
                            }}
                        />
                    </>
                );
            }
            case REGISTER_STEP.USER_PERSONA: {
                return (
                    <UserPersonaCollect
                        personaInfo={personaInfo}
                        personaLabel={personaLabel}
                        setPersonaInfo={setPersonaInfo}
                        suiteId={currentSuiteId}
                        onComplete={() => {
                            if (onComplete) {
                                onComplete();
                            } else {
                                handleRedirect(currentSuiteId);
                            }
                        }}
                    />
                );
            }
        }
    };

    return (
        <AccountPageLayout
            className={classNames('platform-page-register', {
                'is-mobile': isMobileUA()
            })}
            withSuite
            showFooter
            showLangSwitch={registerStep === REGISTER_STEP.REGISTER}
        >
            {showSuiteSelect ? (
                <>
                    <AccountBackButton onClick={redirectToLogin} />
                    <SuiteSelectList
                        className="suite-select"
                        title={Intl.get('account.register.jdy')}
                        description={Intl.get(
                            'fxp.base.workspace.login.please_select_prod_register.tip'
                        )}
                        items={map(suiteViewList, (item) => {
                            return {
                                ...item,
                                suiteId: item.suite_id!
                            };
                        })}
                        onSelect={(item) => {
                            setCurrentSuiteId(item.suiteId);
                            setBackSuiteSelectVisible(true);
                        }}
                    />
                </>
            ) : (
                <>
                    {!isInvitationMode && (
                        <RegisterStep
                            className={classNames({
                                'has-back-btn':
                                    backSuiteSelectVisible &&
                                    (skipRegister
                                        ? registerStep === REGISTER_STEP.CORP_PERSONA
                                        : registerStep === REGISTER_STEP.REGISTER)
                            })}
                            active={registerStep}
                        />
                    )}
                    {renderRegisterStepContent()}
                </>
            )}
        </AccountPageLayout>
    );
};

export default Register;
